import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { DateInputTR } from "@/components/DateInputTR";
import { todayISO } from "@/lib/dateTR";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Receipt, Camera, Sparkle, FloppyDisk, CircleNotch, X, Image as ImageIcon, Info,
} from "@phosphor-icons/react";

const PAYMENT_METHODS = [
  { key: "nakit",          label: "Nakit" },
  { key: "kredi_karti",    label: "Kredi Kartı (kişisel)" },
  { key: "sirket_karti",   label: "Şirket Kartı" },
  { key: "banka_havalesi", label: "Banka Havalesi" },
  { key: "diger",          label: "Diğer" },
];

const VAT_RATES = [0, 1, 8, 10, 18, 20];

export default function ExpenseNew() {
  const nav = useNavigate();
  const { eid } = useParams();
  const isEdit = !!eid;
  const { user } = useAuth();
  const isAdmin = !!user;

  const [categories, setCategories] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [openClaims, setOpenClaims] = useState([]);   // Açık talep formları (draft+submitted)
  const [uploadedFile, setUploadedFile] = useState(null); // {path, filename}
  const [previewUrl, setPreviewUrl] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrData, setOcrData] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    date: todayISO(),
    category_key: "",
    site_id: "__none__",
    amount_gross: "",
    vat_rate: 20,
    payment_method: "nakit",
    description: "",
    vendor_name: "",
    vendor_tax_number: "",
    on_behalf_of_user_id: "__self__",
    attach_claim_id: "__none__",   // Yeni: opsiyonel açık forma ekle
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          axios.get(`${API}/expense-categories`),
          axios.get(`${API}/sites`),
        ]);
        setCategories(c.data || []);
        setSites(s.data || []);
        if (isAdmin) {
          const u = await axios.get(`${API}/users`);
          setUsers(u.data || []);
        }
        if ((c.data || [])[0]) setForm(f => ({ ...f, category_key: c.data[0].key }));

        // Açık talep formlarını çek — sadece yeni masraf oluştururken, düzenlemede gizli
        if (!isEdit) {
          try {
            const oc = await axios.get(`${API}/expense-claims`, {
              params: { status: "draft,submitted" },
            });
            setOpenClaims(oc.data || []);
          } catch (_) { /* sessizce yut — dropdown boş kalır */ }
        }

        // Düzenleme modu: mevcut kaydı yükle
        if (isEdit) {
          const ex = await axios.get(`${API}/expenses/id/${eid}`);
          const d = ex.data;
          setForm({
            date: d.date,
            category_key: d.category_key,
            site_id: d.site_id || "__none__",
            amount_gross: String(d.amount_gross),
            vat_rate: d.vat_rate,
            payment_method: d.payment_method,
            description: d.description || "",
            vendor_name: d.vendor_name || "",
            vendor_tax_number: d.vendor_tax_number || "",
            on_behalf_of_user_id: d.on_behalf_of_user_id || "__self__",
          });
          if (d.invoice_file_path) {
            setUploadedFile({ path: d.invoice_file_path, filename: "önceki fatura" });
            setPreviewUrl(`${API}/files/${d.invoice_file_path}`);
          }
          if (d.ocr_data) setOcrData(d.ocr_data);
        }
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, [isAdmin, isEdit, eid]);

  // KDV hesabı
  const gross = parseFloat(form.amount_gross || 0) || 0;
  const rate = parseFloat(form.vat_rate || 0) || 0;
  const vatAmount = rate > 0 ? +(gross - gross / (1 + rate / 100)).toFixed(2) : 0;
  const netAmount = +(gross - vatAmount).toFixed(2);

  // Açık forma ekleme dropdown'ı için: aynı personel (kim adına) + aynı şantiye
  const attachTargetUserId = (isAdmin && form.on_behalf_of_user_id !== "__self__")
                              ? form.on_behalf_of_user_id : user?.id;
  const attachTargetSiteId = form.site_id === "__none__" ? null : form.site_id;
  const eligibleClaims = openClaims.filter(cl =>
    cl.user_id === attachTargetUserId &&
    (cl.site_id || null) === attachTargetSiteId
  );
  // Şantiye/kim-adına değişince seçili claim geçersiz kalırsa temizle
  useEffect(() => {
    if (form.attach_claim_id !== "__none__" &&
        !eligibleClaims.some(cl => cl.id === form.attach_claim_id)) {
      setForm(f => ({ ...f, attach_claim_id: "__none__" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.site_id, form.on_behalf_of_user_id, openClaims.length]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Dosya çok büyük (max 10 MB)"); return; }
    setPreviewUrl(URL.createObjectURL(f));
    runOCR(f);
  };

  const runOCR = async (f) => {
    setOcrLoading(true);
    setOcrData(null);
    try {
      // 1) Upload permanent copy
      const fd1 = new FormData();
      fd1.append("file", f);
      const up = await axios.post(`${API}/upload`, fd1);
      setUploadedFile({ path: up.data.path, filename: f.name });

      // 2) OCR
      const fd2 = new FormData();
      fd2.append("file", f);
      const { data } = await axios.post(`${API}/expenses/ocr`, fd2);
      const d = data.data || {};
      setOcrData(d);

      // Auto-fill form
      setForm(prev => ({
        ...prev,
        date: d.date || prev.date,
        amount_gross: d.amount_gross != null ? String(d.amount_gross) : prev.amount_gross,
        vat_rate: d.vat_rate != null ? Number(d.vat_rate) : prev.vat_rate,
        category_key: d.category_hint || prev.category_key,
        description: d.description_hint || prev.description,
        vendor_name: d.vendor_name || prev.vendor_name,
        vendor_tax_number: (d.tax_number ? String(d.tax_number).replace(/\D/g, "") : prev.vendor_tax_number),
      }));
      toast.success("Fatura okundu — alanları kontrol edip kaydedin");
    } catch (e) {
      toast.error("AI okuma başarısız: " + formatErr(e));
    } finally { setOcrLoading(false); }
  };

  const clearFile = () => {
    setUploadedFile(null); setPreviewUrl(null); setOcrData(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!form.category_key) { toast.error("Kategori seçin"); return; }
    if (!form.amount_gross || gross <= 0) { toast.error("Tutar girin"); return; }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        category_key: form.category_key,
        site_id: form.site_id === "__none__" ? null : form.site_id,
        amount_gross: gross,
        vat_rate: rate,
        payment_method: form.payment_method,
        description: form.description,
        vendor_name: (form.vendor_name || "").trim(),
        vendor_tax_number: (form.vendor_tax_number || "").replace(/\D/g, ""),
        invoice_file_path: uploadedFile?.path || null,
        ocr_data: ocrData || null,
        on_behalf_of_user_id: (isAdmin && form.on_behalf_of_user_id !== "__self__")
                                ? form.on_behalf_of_user_id : null,
      };
      if (isEdit) {
        await axios.put(`${API}/expenses/id/${eid}`, payload);
        toast.success("Masraf güncellendi");
      } else {
        const { data: created } = await axios.post(`${API}/expenses`, payload);
        // Opsiyonel: seçili açık forma ekle
        if (form.attach_claim_id && form.attach_claim_id !== "__none__") {
          try {
            const cid = form.attach_claim_id;
            const { data: updClaim } = await axios.post(
              `${API}/expense-claims/id/${cid}/attach-expenses`,
              { expense_ids: [created.id] }
            );
            toast.success(`Masraf ${updClaim.code} formuna eklendi`);
          } catch (e2) {
            toast.error("Masraf kaydedildi ama forma eklenemedi: " + formatErr(e2));
          }
        } else {
          toast.success("Masraf kaydedildi — onay bekliyor");
        }
      }
      nav("/masraf/liste");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Shell title={isEdit ? "Masraf Düzenle" : "Yeni Masraf Bildirimi"} subtitle="Masraf Bildirimi › Yeni Kayıt" hideSiteSelector>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Sol: Foto + OCR */}
        <Card className="p-4 rounded-none sm:rounded-sm lg:col-span-2 border-slate-200" data-testid="expense-photo-card">
          <div className="flex items-center gap-2 mb-3">
            <Camera size={20} weight="duotone" className="text-[#95C11F]" />
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700">Fatura / Fiş</div>
          </div>

          {!previewUrl ? (
            <label className="block cursor-pointer border-2 border-dashed border-slate-300 hover:border-[#95C11F] transition-colors p-8 text-center">
              <input
                type="file" ref={fileRef} accept="image/*" onChange={onFileChange}
                className="hidden" data-testid="expense-photo-input"
              />
              <ImageIcon size={40} weight="duotone" className="mx-auto text-slate-400 mb-2" />
              <div className="text-sm font-bold text-slate-700">Fatura fotoğrafı seç veya çek</div>
              <div className="text-xs text-slate-500 mt-1">Yapay zeka alanları otomatik dolduracak</div>
            </label>
          ) : (
            <div className="relative">
              <img src={previewUrl} alt="fatura" className="w-full max-h-96 object-contain border border-slate-200" />
              <button type="button" onClick={clearFile} data-testid="expense-photo-clear"
                      className="absolute top-2 right-2 bg-white/95 hover:bg-white p-1.5 rounded-full shadow">
                <X size={14} />
              </button>
              {ocrLoading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center flex-col">
                  <CircleNotch size={32} className="animate-spin text-[#95C11F] mb-2" />
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-600">Fatura okunuyor…</div>
                </div>
              )}
            </div>
          )}

          {ocrData && (
            <div className="mt-3 p-3 bg-[#F7FBE9] border-l-4 border-[#95C11F]">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#1B4776] mb-1">
                <Sparkle size={12} weight="fill" /> AI Sonucu
              </div>
              <div className="text-xs space-y-0.5 text-slate-700">
                {ocrData.vendor_name && <div><b>Firma:</b> {ocrData.vendor_name}</div>}
                {ocrData.tax_number && <div><b>VKN:</b> {ocrData.tax_number}</div>}
                {ocrData.confidence && (
                  <div className="text-[10px] italic text-slate-500 mt-1">
                    Güven: tarih={ocrData.confidence.date} · tutar={ocrData.confidence.amount_gross}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-3 text-[10px] flex items-start gap-1 text-slate-500 italic leading-relaxed">
            <Info size={12} className="mt-0.5 shrink-0" />
            <span>Fatura yüklemek zorunlu değil. Yüklerseniz sistem alanları otomatik doldurmayı dener; siz onaylamadan hiçbir şey kaydedilmez.</span>
          </div>
        </Card>

        {/* Sağ: Form */}
        <Card className="p-5 rounded-none sm:rounded-sm lg:col-span-3 border-slate-200">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200">
            <Receipt size={20} weight="duotone" className="text-[#1B4776]" />
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700">Masraf Bilgileri</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Tarih *</Label>
              <DateInputTR value={form.date} onChange={v => setForm({ ...form, date: v })}
                           data-testid="expense-date" className="rounded-none sm:rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Kategori *</Label>
              <Select value={form.category_key} onValueChange={v => setForm({ ...form, category_key: v })}>
                <SelectTrigger data-testid="expense-category" className="rounded-none sm:rounded-sm mt-1">
                  <SelectValue placeholder="Seçin…" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Şantiye</Label>
              <Select value={form.site_id} onValueChange={v => setForm({ ...form, site_id: v })}>
                <SelectTrigger data-testid="expense-site" className="rounded-none sm:rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Genel (şantiyeye özel değil) —</SelectItem>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Talep Formu (opsiyonel) — sadece yeni masraf oluştururken göster */}
            {!isEdit && (
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 flex items-center gap-1">
                  Talep Formu <span className="text-slate-400 normal-case tracking-normal">(opsiyonel — henüz onaylanmamış açık form)</span>
                </Label>
                <Select value={form.attach_claim_id}
                        onValueChange={v => setForm({ ...form, attach_claim_id: v })}
                        disabled={eligibleClaims.length === 0}>
                  <SelectTrigger data-testid="expense-attach-claim" className="rounded-none sm:rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Forma ekleme, ayrı kayıt olarak tut —</SelectItem>
                    {eligibleClaims.map(cl => {
                      const statusLbl = cl.status === "submitted" ? "Beklemede" : "Taslak";
                      return (
                        <SelectItem key={cl.id} value={cl.id}>
                          <span className="font-mono">{cl.code}</span>
                          <span className="text-slate-500"> · {statusLbl}</span>
                          <span className="text-slate-500"> · ₺{Number(cl.total_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {eligibleClaims.length === 0
                    ? "Aynı personel + aynı şantiye için açık form yok. Boş bırakıp kaydedebilir, sonra listeden forma ekleyebilirsiniz."
                    : `${eligibleClaims.length} açık form bulundu.`}
                </div>
              </div>
            )}

            {/* Firma bilgileri — fatura yüklendiyse AI ile otomatik dolar */}
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Firma (Ticari Unvan)</Label>
              <Input value={form.vendor_name}
                     onChange={e => setForm({ ...form, vendor_name: e.target.value })}
                     data-testid="expense-vendor-name"
                     placeholder="Fatura üzerindeki firma unvanı"
                     className="rounded-none sm:rounded-sm mt-1" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">VKN / TCKN</Label>
              <Input value={form.vendor_tax_number}
                     onChange={e => setForm({ ...form, vendor_tax_number: e.target.value.replace(/\D/g, "").slice(0, 11) })}
                     data-testid="expense-vendor-tax"
                     inputMode="numeric"
                     placeholder="10 veya 11 hane"
                     maxLength={11}
                     className="rounded-none sm:rounded-sm mt-1 font-mono" />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Brüt Tutar (₺, KDV dahil) *</Label>
              <Input type="number" step="0.01" min="0" inputMode="decimal"
                     value={form.amount_gross} onChange={e => setForm({ ...form, amount_gross: e.target.value })}
                     data-testid="expense-amount" className="rounded-none sm:rounded-sm mt-1 font-mono text-lg font-bold" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">KDV Oranı</Label>
              <Select value={String(form.vat_rate)} onValueChange={v => setForm({ ...form, vat_rate: Number(v) })}>
                <SelectTrigger data-testid="expense-vat" className="rounded-none sm:rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VAT_RATES.map(r => <SelectItem key={r} value={String(r)}>%{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* KDV özeti */}
            <div className="col-span-2 grid grid-cols-3 gap-2 bg-slate-50 p-2 border border-slate-200">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-slate-500">Net</div>
                <div className="text-sm font-mono font-bold text-slate-800" data-testid="expense-calc-net">
                  ₺{netAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-slate-500">KDV</div>
                <div className="text-sm font-mono font-bold text-amber-700" data-testid="expense-calc-vat">
                  ₺{vatAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-slate-500">Brüt</div>
                <div className="text-sm font-mono font-bold text-[#1B4776]" data-testid="expense-calc-gross">
                  ₺{gross.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ödeme Şekli</Label>
              <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="expense-payment" className="rounded-none sm:rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {isAdmin && (
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Kim Adına?</Label>
                <Select value={form.on_behalf_of_user_id} onValueChange={v => setForm({ ...form, on_behalf_of_user_id: v })}>
                  <SelectTrigger data-testid="expense-onbehalf" className="rounded-none sm:rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__self__">Kendi adıma</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Açıklama</Label>
              <Textarea rows={3} value={form.description}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                        data-testid="expense-description"
                        className="rounded-none sm:rounded-sm mt-1"
                        placeholder="Kısa açıklama (ör: X inşaatına araç yakıtı)" />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={() => nav("/masraf/liste")}
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
              Vazgeç
            </Button>
            <Button onClick={submit} disabled={saving} data-testid="expense-submit"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#1B4776] hover:bg-[#153859] text-white">
              {saving ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <FloppyDisk size={14} className="mr-1.5" />}
              Kaydet
            </Button>
          </div>
        </Card>
      </div>
    </Shell>
  );
}
